import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail, Inbox, Send as SendIcon, Trash2, Star, Search, Paperclip, X, Plus,
  RefreshCw, Reply, ReplyAll, Forward, LogOut, Users, ChevronLeft, ChevronRight,
  Download, Bold, Italic, Underline, List, ListOrdered, Link2, Quote, Eraser,
  ShieldAlert, Loader2, Archive, FileText, ArrowUpDown, Check, Square, CheckSquare,
  MailOpen, Keyboard, Filter, MessagesSquare, BellRing, Sun, Moon, Settings, Wand2, ArrowLeft,
} from "lucide-react";
import PushToggle from "../components/PushToggle";
import { useTheme } from "../context/ThemeContext";

/* ---------------------------------------------------------------------------
   Webmail, reached from the CRM's Email link or by typing <site>/email.

   It keeps its own session: a mailbox is not a CRM user, so this page stores a
   separate "mail-token" and never touches the CRM's. Signing in here grants no
   CRM access, and being a CRM agent grants no mailbox access.
--------------------------------------------------------------------------- */

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api/mail`;
const TOKEN_KEY = "mail-token";
const PREF_KEY = "mail-prefs";

const tok = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};
/* The CRM's own session, read only to offer the bridge below. The mail client
   still keeps its own token; this is not a second way to be signed in, it is a
   way to get one. */
const crmTok = { get: () => localStorage.getItem("crm-token") };
const NOAUTO_KEY = "mail-no-auto";

/* Exchange a CRM sign-in for a mail session. Succeeds only for an account the
   server considers entitled to every mailbox; for everyone else it fails with
   the same message, and this page simply shows its own sign-in form. */
async function tryCrmSession() {
  const t = crmTok.get();
  if (!t || localStorage.getItem(NOAUTO_KEY)) return null;
  try {
    const res = await fetch(`${API}/crm-session`, {
      method: "POST", headers: { Authorization: `Bearer ${t}` },
    });
    if (!res.ok) return null;
    const d = await res.json();
    if (!d.token) return null;
    tok.set(d.token);
    return d.mailbox;
  } catch { return null; }
}

const prefs = {
  get: () => { try { return JSON.parse(localStorage.getItem(PREF_KEY) || "{}"); } catch { return {}; } },
  set: (p) => { try { localStorage.setItem(PREF_KEY, JSON.stringify(p)); } catch { /* private mode */ } },
};

async function call(path, { method = "GET", json, form } = {}) {
  const headers = {};
  const t = tok.get();
  if (t) headers.Authorization = `Bearer ${t}`;
  if (json) headers["Content-Type"] = "application/json";
  const res = await fetch(API + path, { method, headers, body: json ? JSON.stringify(json) : form || undefined });
  if (res.status === 401) {
    tok.clear();
    window.dispatchEvent(new Event("mail-signed-out"));
    throw new Error("Your session expired. Please sign in again.");
  }
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = null; }
  if (!res.ok) {
    const d = data && data.detail;
    throw new Error(typeof d === "string" ? d : `Request failed (${res.status}).`);
  }
  return data;
}

/* ---------- helpers ---------- */
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString([], { month: "short", day: "numeric" });
  return d.toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
};
const fmtFull = (iso) => { const d = new Date(iso); return isNaN(d) ? "" : d.toLocaleString(); };
const fmtBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const initials = (s) => (s || "?").trim().slice(0, 1).toUpperCase();
const displayFrom = (m) => m.from_name || (m.from_addr || "").split("@")[0] || "(unknown)";
/* Stable per-sender colour, so the same person always looks the same. */
const AV = ["bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300",
            "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
            "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
            "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
            "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300",
            "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300"];
const avatarOf = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AV[h % AV.length];
};

/* Email HTML is hostile by default. The frame that renders it gets no
   allow-scripts, but we still strip the obvious weapons, and we hold remote
   images back until asked - loading them silently confirms the address to a
   sender and leaks the read to a tracking pixel, which is why Gmail blocks
   them too. */
function sanitizeEmailHtml(html, showImages, attachments) {
  let out = String(html || "");
  out = out.replace(/<\s*(script|iframe|object|embed|link|meta|base)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, "");
  out = out.replace(/<\s*(script|iframe|object|embed|link|meta|base)\b[^>]*\/?>/gi, "");
  out = out.replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, "");
  out = out.replace(/\son[a-z]+\s*=\s*'[^']*'/gi, "");
  out = out.replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, "");
  out = out.replace(/javascript:/gi, "blocked:");
  let blocked = 0;
  if (!showImages) {
    out = out.replace(/<img\b([^>]*?)\ssrc\s*=\s*("|')(https?:\/\/[^"']*)\2([^>]*)>/gi,
      (_m, a, _q, url, b) => { blocked += 1; return `<img${a} data-blocked="${url}"${b} style="display:none">`; });
    out = out.replace(/\ssrcset\s*=\s*("|')[^"']*\1/gi, "");
    out = out.replace(/url\((\s*['"]?https?:\/\/[^)]*)\)/gi, () => { blocked += 1; return "none"; });
  }

  // Inline images arrive as <img src="cid:SOMETHING"> with the picture itself
  // sitting in the attachment list under that Content-ID. Point each one at the
  // copy we already hold. Done last, so the rewritten URLs - which may be
  // absolute - are never mistaken for remote images by the blocker above.
  const byCid = new Map();
  (attachments || []).forEach((a) => { if (a && a.cid) byCid.set(String(a.cid), a); });
  const inlined = new Set();
  const t = encodeURIComponent(tok.get() || "");
  out = out.replace(/(<img\b[^>]*?\ssrc\s*=\s*)("|')cid:([^"']+)\2/gi, (m, head, q, cid) => {
    const a = byCid.get(String(cid).trim());
    if (!a) return m;                 // nothing matches: leave it, hidden below
    inlined.add(a.id);
    return `${head}${q}${API}/attachments/${a.id}?t=${t}${q}`;
  });
  // A cid: that matched nothing would render as a broken-image icon in the
  // middle of the message. Hide it - the file, if there is one, is still listed
  // underneath.
  out = out.replace(/<img\b([^>]*?)\ssrc\s*=\s*("|')cid:[^"']*\2([^>]*)>/gi,
    (_m, a, _q, b) => `<img${a}${b} style="display:none">`);

  return { html: out, blocked, inlined };
}

function frameDoc(html, dark) {
  return `<!doctype html><html><head><meta charset="utf-8">
<base target="_blank" rel="noopener noreferrer">
<style>
 html,body{margin:0;padding:0}
 /* 16px each side: the same inset px-4 gives every other row in this pane -
    the sender line, the attachment list, the blocked-images notice. At the 2px
    this had, the message was the only thing touching the edge of the pane. */
 body{font:14px/1.55 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif;
      color:${dark ? "#e2e8f0" : "#111827"};background:${dark ? "#0f172a" : "#fff"};word-wrap:break-word;
      padding:2px 16px 16px}
 img{max-width:100%;height:auto}
 table{max-width:100%}
 blockquote{margin:0 0 0 .8em;padding-left:.8em;border-left:2px solid ${dark ? "#334155" : "#d1d5db"};color:${dark ? "#94a3b8" : "#4b5563"}}
 a{color:${dark ? "#60a5fa" : "#2563eb"}}
 pre{white-space:pre-wrap;word-wrap:break-word;font-family:ui-monospace,monospace}
</style></head><body>${html}</body></html>`;
}

const SORTS = [
  { k: "date_desc", l: "Newest first" },
  { k: "date_asc", l: "Oldest first" },
  { k: "unread_first", l: "Unread first" },
  { k: "starred_first", l: "Starred first" },
  { k: "from_asc", l: "Sender A–Z" },
  { k: "from_desc", l: "Sender Z–A" },
  { k: "subject_asc", l: "Subject A–Z" },
  { k: "subject_desc", l: "Subject Z–A" },
  { k: "size_desc", l: "Largest first" },
  { k: "size_asc", l: "Smallest first" },
];

function BackToCrm() {
  const nav = useNavigate();
  if (!crmTok.get()) return null;     // signed in with a mailbox password alone
  return (
    <button onClick={() => nav("/dashboard")} data-testid="mail-back-to-crm"
      title="Back to the CRM" aria-label="Back to the CRM"
      className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400">
      <ArrowLeft size={15} /> <span className="hidden sm:inline">Back to CRM</span>
    </button>
  );
}

function ThemeButton() {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <button onClick={toggle} data-testid="mail-theme"
      title={dark ? "Switch to light mode" : "Switch to dark mode"}
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      className="text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400">
      {dark ? <Sun size={17} /> : <Moon size={17} />}
    </button>
  );
}

/* =========================== sign in =========================== */
function SignIn({ onDone }) {
  const [address, setAddress] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const d = await call("/login", { method: "POST", json: { address, password } });
      tok.set(d.token);
      onDone(d.mailbox);
    } catch (ex) { setErr(ex.message || "Could not sign in."); }
    finally { setBusy(false); }
  };

  // Signing out sets a flag so the CRM bridge does not sign you straight back
  // in. This is how you deliberately ask for it again - and it is offered to
  // every CRM account, refusing all but the entitled one with one message.
  const useCrm = async () => {
    setBusy(true); setErr("");
    try { localStorage.removeItem(NOAUTO_KEY); } catch { /* private mode */ }
    const box = await tryCrmSession();
    setBusy(false);
    if (box) onDone(box);
    else {
      try { localStorage.setItem(NOAUTO_KEY, "1"); } catch { /* private mode */ }
      setErr("Your CRM sign-in does not open mailboxes.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0B0F19] p-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-7 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <span className="h-10 w-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center"><Mail size={20} /></span>
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-white leading-tight">Mail</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">Sign in to your mailbox</p>
          </div>
          <div className="ml-auto flex items-center gap-3"><BackToCrm /><ThemeButton /></div>
        </div>
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Email address</label>
        <input value={address} onChange={(e) => setAddress(e.target.value)} autoFocus autoComplete="username"
          placeholder="you@yourdomain.com" data-testid="mail-address"
          className="w-full mb-4 px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 outline-none focus:border-indigo-500" />
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Password</label>
        <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="current-password"
          placeholder="••••••••" data-testid="mail-password"
          className="w-full mb-5 px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 outline-none focus:border-indigo-500" />
        {err && <p className="text-sm text-rose-600 dark:text-rose-400 mb-4" role="alert">{err}</p>}
        <button type="submit" disabled={busy || !address || !password} data-testid="mail-signin"
          className="w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} Sign in
        </button>
        {crmTok.get() && (
          <>
            <div className="flex items-center gap-3 my-5">
              <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
              <span className="text-[11px] uppercase tracking-wide text-slate-400">or</span>
              <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
            </div>
            <button type="button" onClick={useCrm} disabled={busy} data-testid="mail-use-crm"
              className="w-full py-2.5 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-medium hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-center gap-2">
              Continue with my CRM sign-in
            </button>
          </>
        )}
      </form>
    </div>
  );
}

/* =========================== compose =========================== */
/* --------------------------------------------------------------------------
   Pasting from somewhere else.

   Text copied out of Word, a web page or another mail client arrives wearing
   that source's clothes: a white background it needs, a font nobody else has,
   and a text colour chosen for a dark page which is unreadable on the white one
   a client will actually read the mail on. Keeping any of it is a bet on the
   recipient's mail client, and it is a bet that loses quietly.

   So the structure is kept - paragraphs, lists, links, bold, italic, quotes -
   and the presentation is thrown away, leaving the message to inherit the
   styling of the mail it is being written into.
--------------------------------------------------------------------------- */
const PASTE_KEEP = {
  P: "P", DIV: "P", BR: "BR", STRONG: "STRONG", B: "STRONG", EM: "EM", I: "EM",
  U: "U", A: "A", UL: "UL", OL: "OL", LI: "LI", BLOCKQUOTE: "BLOCKQUOTE",
  H1: "H", H2: "H", H3: "H", H4: "H", H5: "H", H6: "H",
  TR: "P", TD: "CELL", TH: "CELL", TABLE: "SKIN", TBODY: "SKIN", THEAD: "SKIN", TFOOT: "SKIN",
};
const PASTE_BLOCKS = "p,div,ul,ol,li,table,tr,blockquote,h1,h2,h3,h4,h5,h6";
const BLOCK_OUT = { P: 1, UL: 1, OL: 1, LI: 1, BLOCKQUOTE: 1 };

function cleanPastedHtml(html) {
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  const out = doc.createElement("div");

  const walk = (src, dst) => {
    for (const node of Array.from(src.childNodes)) {
      if (node.nodeType === 3) {
        // Word indents with runs of non-breaking spaces. They are spaces, not
        // line breaks - turning them into breaks rewrites the sender's words.
        dst.appendChild(doc.createTextNode(node.nodeValue.replace(/ /g, " ")));
        continue;
      }
      if (node.nodeType !== 1) continue;          // comments and the rest
      const tag = node.tagName.toUpperCase();
      if (tag === "SCRIPT" || tag === "STYLE" || tag === "HEAD" || tag === "META" || tag === "LINK") continue;

      const keep = PASTE_KEEP[tag];
      if (!keep || keep === "SKIN") { walk(node, dst); continue; }   // SPAN, FONT, TABLE shell
      if (keep === "BR") { dst.appendChild(doc.createElement("br")); continue; }
      if (keep === "CELL") {                       // a cell is text, spaced from its neighbour
        if (dst.textContent && !/\s$/.test(dst.textContent)) dst.appendChild(doc.createTextNode(" "));
        walk(node, dst);
        continue;
      }
      // A container that holds other blocks is scaffolding, not a paragraph:
      // wrapping it would nest <p> inside <p>, which no mail client agrees on.
      if (keep === "P" && tag !== "P" && node.querySelector(PASTE_BLOCKS)) { walk(node, dst); continue; }
      // Nor may a block land inside one. Hoist it to the nearest home that can
      // hold it, which is how the browser would have parsed it anyway.
      let target = dst;
      if (BLOCK_OUT[keep] && BLOCK_OUT[dst.tagName] && keep !== "LI") {
        target = dst.parentNode || dst;
      }
      if (keep === "H") {
        const p = doc.createElement("p");
        const s = doc.createElement("strong");
        walk(node, s);
        p.appendChild(s);
        if (s.textContent.trim()) target.appendChild(p);
        continue;
      }
      const el = doc.createElement(keep);
      if (keep === "A") {
        const href = (node.getAttribute("href") || "").trim();
        if (!/^(https?:|mailto:)/i.test(href)) { walk(node, dst); continue; }
        el.setAttribute("href", href);
        el.setAttribute("target", "_blank");
        el.setAttribute("rel", "noopener noreferrer");
      }
      // Everything else is dropped on purpose: style, class, colour, background,
      // width, face, size, id, and every on* handler with them.
      walk(node, el);
      if (el.textContent.trim() || keep === "A") target.appendChild(el);
    }
  };
  walk(doc.body, out);

  out.querySelectorAll("p").forEach((p) => {
    if (!p.textContent.trim() && !p.querySelector("br, a")) p.remove();
  });
  // Only runs of breaks collapse. Runs of spaces are the writer's spacing.
  return out.innerHTML.replace(/(?:<br\s*\/?>\s*){3,}/gi, "<br><br>");
}

function plainToHtml(text) {
  const esc = String(text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc.split(/\n{2,}/).map((para) =>
    "<p>" + para.split(/\n/).join("<br>") + "</p>").join("");
}

function Compose({ me, sendAs, boxes, initial, onClose, onSent }) {
  const [to, setTo] = useState(initial?.to || "");
  const [cc, setCc] = useState(initial?.cc || "");
  const [showCc, setShowCc] = useState(!!initial?.cc);
  const [subject, setSubject] = useState(initial?.subject || "");
  const [files, setFiles] = useState([]);
  const [keptFiles, setKeptFiles] = useState(initial?.attachments || []);
  const [draftId, setDraftId] = useState(initial?.draftId || null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState("");
  const [err, setErr] = useState("");
  const body = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { if (body.current) body.current.innerHTML = initial?.html || ""; }, [initial]);

  const exec = (cmd, val) => { body.current?.focus(); try { document.execCommand(cmd, false, val); } catch { /* older engines */ } };
  const addLink = () => {
    const url = window.prompt("Link URL");
    if (url) exec("createLink", /^https?:\/\//i.test(url) ? url : `https://${url}`);
  };
  const pickFiles = (list) => { const arr = Array.from(list || []); if (arr.length) setFiles((f) => [...f, ...arr]); };

  // On by default: pasting a client's own words back at them in the colours of
  // whatever page they came from is the common case, and the wrong one.
  const [cleanPaste, setCleanPaste] = useState(() => prefs.get().cleanPaste !== false);
  useEffect(() => { prefs.set({ ...prefs.get(), cleanPaste }); }, [cleanPaste]);

  const onPaste = (e) => {
    if (!cleanPaste) return;                       // let the browser paste it as-is
    const dt = e.clipboardData;
    if (!dt) return;
    const html = dt.getData("text/html");
    const text = dt.getData("text/plain");
    if (!html && !text) return;
    e.preventDefault();
    const out = html ? cleanPastedHtml(html) : plainToHtml(text);
    body.current?.focus();
    try { document.execCommand("insertHTML", false, out); }
    catch { document.execCommand("insertText", false, text || ""); }
  };
  const totalSize = files.reduce((a, f) => a + f.size, 0) + keptFiles.reduce((a, f) => a + (f.size || 0), 0);

  // Which address this will actually leave from - worth naming, since an
  // administrator sending from somebody else's mailbox should see whose.
  const fromAddr = (sendAs && (boxes || []).find((b) => b.id === sendAs)?.address) || me.address || "";

  const buildForm = () => {
    const fd = new FormData();
    fd.append("to", to); fd.append("cc", cc); fd.append("subject", subject);
    fd.append("html", body.current ? body.current.innerHTML : "");
    fd.append("text", body.current ? body.current.innerText : "");
    if (sendAs && sendAs !== me.id) fd.append("mailbox", sendAs);
    if (initial?.inReplyTo) fd.append("in_reply_to", initial.inReplyTo);
    if (draftId) fd.append("draft_id", draftId);
    files.forEach((f) => fd.append("files", f, f.name));
    return fd;
  };

  const saveDraft = async (quiet) => {
    if (busy) return null;
    const hasSomething = to.trim() || subject.trim() || (body.current && body.current.innerText.trim()) || files.length;
    if (!hasSomething) return null;
    setSaving("Saving…");
    try {
      const d = await call("/draft", { method: "POST", form: buildForm() });
      if (d?.id) { setDraftId(d.id); setFiles([]); }
      setSaving("Saved");
      setTimeout(() => setSaving(""), 1500);
      return d?.id || null;
    } catch (ex) { if (!quiet) setErr(ex.message); setSaving(""); return null; }
  };

  const submit = async () => {
    if (busy) return;
    if (!to.trim()) { setErr("Add at least one recipient."); return; }
    setBusy(true); setErr("");
    try {
      await call("/send", { method: "POST", form: buildForm() });
      onSent();
    } catch (ex) { setErr(ex.message || "Could not send."); }
    finally { setBusy(false); }
  };

  const closeWithSave = async () => { await saveDraft(true); onClose(true); };

  const Tool = ({ icon: Icon, cmd, val, onClick, title }) => (
    <button type="button" title={title} aria-label={title} onMouseDown={(e) => e.preventDefault()}
      onClick={onClick || (() => exec(cmd, val))}
      className="h-8 w-8 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 flex items-center justify-center">
      <Icon size={15} />
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={closeWithSave}>
      <div onClick={(e) => e.stopPropagation()} data-testid="mail-compose"
        className="bg-white dark:bg-slate-900 w-full sm:max-w-4xl sm:h-[78vh] sm:rounded-2xl rounded-t-2xl border border-slate-200 dark:border-slate-800 shadow-xl flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <h2 className="font-semibold text-slate-900 dark:text-white">{draftId ? "Draft" : "New message"}</h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400" data-testid="mail-draft-state">{saving}</span>
            <button onClick={() => saveDraft(false)} data-testid="mail-savedraft"
              className="text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-600">Save draft</button>
            <button onClick={closeWithSave} aria-label="Close" className="text-slate-400 hover:text-slate-700 dark:hover:text-white"><X size={18} /></button>
          </div>
        </div>

        <div className="px-4 pt-3 space-y-2">
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
            <span className="text-xs text-slate-500 dark:text-slate-400 w-10">To</span>
            <input value={to} onChange={(e) => setTo(e.target.value)} data-testid="mail-to"
              placeholder="name@example.com, another@example.com"
              className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-900 dark:text-slate-100" />
            {!showCc && <button onClick={() => setShowCc(true)} className="text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-600">Cc</button>}
          </div>
          {showCc && (
            <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
              <span className="text-xs text-slate-500 dark:text-slate-400 w-10">Cc</span>
              <input value={cc} onChange={(e) => setCc(e.target.value)} data-testid="mail-cc"
                className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-900 dark:text-slate-100" />
            </div>
          )}
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
            <span className="text-xs text-slate-500 dark:text-slate-400 w-10">Subject</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="mail-subject"
              className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-900 dark:text-slate-100" />
          </div>
        </div>

        <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-slate-200 dark:border-slate-800 flex-wrap">
          <Tool icon={Bold} cmd="bold" title="Bold" />
          <Tool icon={Italic} cmd="italic" title="Italic" />
          <Tool icon={Underline} cmd="underline" title="Underline" />
          <span className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
          <Tool icon={List} cmd="insertUnorderedList" title="Bulleted list" />
          <Tool icon={ListOrdered} cmd="insertOrderedList" title="Numbered list" />
          <Tool icon={Quote} cmd="formatBlock" val="blockquote" title="Quote" />
          <span className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
          <Tool icon={Link2} onClick={addLink} title="Insert link" />
          <Tool icon={Eraser} cmd="removeFormat" title="Clear formatting" />
          <button type="button" onClick={() => setCleanPaste((v) => !v)} data-testid="mail-cleanpaste"
            aria-pressed={cleanPaste}
            title={cleanPaste
              ? "Pasted text is stripped of its original colours, fonts and backgrounds and restyled to match this email. Click to paste with source formatting instead."
              : "Pasted text keeps its original formatting, which often does not survive a client's mail app. Click to clean it instead."}
            className={`p-1.5 rounded ${cleanPaste
              ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300"
              : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
            <Wand2 size={15} />
          </button>
          <span className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
          <Tool icon={Paperclip} onClick={() => fileRef.current?.click()} title="Attach files" />
          <input ref={fileRef} type="file" multiple hidden onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }} />
        </div>

        <div ref={body} contentEditable suppressContentEditableWarning data-testid="mail-body"
          onPaste={onPaste}
          onDrop={(e) => { if (e.dataTransfer?.files?.length) { e.preventDefault(); pickFiles(e.dataTransfer.files); } }}
          className="flex-1 overflow-y-auto px-4 py-3 min-h-[240px] outline-none text-sm text-slate-900 dark:text-slate-100" />

        {(files.length > 0 || keptFiles.length > 0) && (
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {keptFiles.map((f, i) => (
              <span key={`k${i}`} className="inline-flex items-center gap-2 text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1">
                <Paperclip size={12} /><span className="max-w-[160px] truncate">{f.name}</span>
                <span className="text-slate-400">{fmtBytes(f.size)}</span>
              </span>
            ))}
            {files.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-2 text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1">
                <Paperclip size={12} /><span className="max-w-[160px] truncate">{f.name}</span>
                <span className="text-slate-400">{fmtBytes(f.size)}</span>
                <button onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} aria-label="Remove" className="text-slate-400 hover:text-rose-500"><X size={12} /></button>
              </span>
            ))}
            <span className="text-xs text-slate-400 dark:text-slate-500 self-center">{fmtBytes(totalSize)} total</span>
          </div>
        )}

        {err && <p className="px-4 pb-2 text-sm text-rose-600 dark:text-rose-400" role="alert">{err}</p>}

        <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-200 dark:border-slate-800">
          <button onClick={submit} disabled={busy} data-testid="mail-send"
            className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-400 text-white text-sm font-semibold flex items-center gap-2">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <SendIcon size={15} />} Send
          </button>
          <span className="text-xs text-slate-400 truncate">from {fromAddr || "no mailbox selected"}</span>
        </div>
      </div>
    </div>
  );
}

/* =========================== mailbox admin =========================== */
function Mailboxes({ me, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ address: "", password: "", name: "", app: "", is_admin: false });
  const canCreate = !!me?.can_create;

  const load = useCallback(async () => {
    try { setRows((await call("/mailboxes")).mailboxes || []); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Who a mailbox can be handed to. Anyone allowed in this panel can assign.
    call("/assignable-users").then((d) => setPeople(d.users || [])).catch(() => setPeople([]));
  }, []);

  const create = async () => {
    setBusy(true); setErr("");
    try {
      await call("/mailboxes", { method: "POST", json: f });
      setF({ address: "", password: "", name: "", app: "", is_admin: false });
      await load(); onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.address} and all of its mail? This cannot be undone.`)) return;
    try { await call(`/mailboxes/${r.id}`, { method: "DELETE" }); await load(); onChanged(); } catch (e) { setErr(e.message); }
  };
  const resetPw = async (r) => {
    const pw = window.prompt(`New password for ${r.address} (min 8 characters)`);
    if (!pw) return;
    try { await call(`/mailboxes/${r.id}`, { method: "PATCH", json: { password: pw } }); alert("Password updated."); } catch (e) { setErr(e.message); }
  };
  const patchBox = async (r, patch) => {
    setErr("");
    try { await call(`/mailboxes/${r.id}`, { method: "PATCH", json: patch }); await load(); onChanged(); }
    catch (e) { setErr(e.message); }
  };
  const emptyBin = async (r) => {
    if (!window.confirm(`Permanently delete the ${r.trash} message(s) in ${r.address}'s bin?`)) return;
    setErr("");
    try {
      const d = await call(`/mailboxes/${r.id}/empty-bin`, { method: "POST" });
      await load(); onChanged();
      alert(`Removed ${d.removed} message(s), freeing ${Math.round((d.freed_bytes || 0) / 1024)} KB.`);
    } catch (e) { setErr(e.message); }
  };
  const saveAssignees = async (r, ids) => {
    setErr("");
    try {
      await call(`/mailboxes/${r.id}/assignees`, { method: "PUT", json: { user_ids: ids } });
      await load(); onChanged();
    } catch (e) { setErr(e.message); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="mail-boxes"
        className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl flex flex-col max-h-[88vh]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800">
          <div>
            <h2 className="font-semibold text-slate-900 dark:text-white">Mailboxes</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {canCreate ? "Create mailboxes and choose who reads them."
                         : "Choose who reads each mailbox."}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700 dark:hover:text-white"><X size={18} /></button>
        </div>

        {canCreate && (
          <div className="p-5 border-b border-slate-200 dark:border-slate-800">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">Create a mailbox</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="name@yourdomain.com" data-testid="box-address"
                className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm text-slate-900 dark:text-slate-100 outline-none" />
              <input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="password (min 8)" data-testid="box-password"
                className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm text-slate-900 dark:text-slate-100 outline-none" />
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Display name"
                className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm text-slate-900 dark:text-slate-100 outline-none" />
              <input value={f.app} onChange={(e) => setF({ ...f, app: e.target.value })} placeholder="App / label (optional)"
                className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm text-slate-900 dark:text-slate-100 outline-none" />
            </div>
            <div className="flex items-center justify-between mt-3">
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                <input type="checkbox" checked={f.is_admin} onChange={(e) => setF({ ...f, is_admin: e.target.checked })} />
                Administrator (can manage and read every mailbox)
              </label>
              <button onClick={create} disabled={busy} data-testid="box-create"
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-400 text-white text-sm font-semibold flex items-center gap-2">
                <Plus size={15} /> Create
              </button>
            </div>
          </div>
        )}
        {err && <p className="text-sm text-rose-600 dark:text-rose-400 px-5 pt-3">{err}</p>}

        <div className="overflow-y-auto p-2">
          {rows.map((r) => (
            <BoxRow key={r.id} r={r} people={people} canCreate={canCreate}
              onAssign={(ids) => saveAssignees(r, ids)}
              onPatch={(patch) => patchBox(r, patch)}
              onEmptyBin={() => emptyBin(r)}
              onResetPw={() => resetPw(r)} onRemove={() => remove(r)} />
          ))}
          {!rows.length && <p className="text-sm text-slate-500 dark:text-slate-400 p-4 text-center">No mailboxes yet.</p>}
        </div>
      </div>
    </div>
  );
}

function BoxRow({ r, people, canCreate, onAssign, onPatch, onResetPw, onRemove, onEmptyBin }) {
  const [tab, setTab] = useState(null);          // null | "who" | "settings"
  const [draft, setDraft] = useState(r);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(r); }, [r]);

  const assigned = r.assignees || [];
  const ids = assigned.map((a) => a.id);
  const toggleUser = (uid) => onAssign(ids.includes(uid) ? ids.filter((x) => x !== uid) : [...ids, uid]);

  const quotaBytes = (Number(draft.quota_mb) || 0) * 1048576;
  const used = Number(r.used_bytes) || 0;
  const pct = quotaBytes > 0 ? Math.min(100, Math.round((used / quotaBytes) * 100)) : 0;
  const full = quotaBytes > 0 && used >= quotaBytes;
  const near = quotaBytes > 0 && !full && pct >= 80;

  const save = async (patch) => {
    setSaving(true);
    try { await onPatch(patch); } finally { setSaving(false); }
  };

  return (
    <div className="px-3 py-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`box-row-${r.id}`}>
      <div className="flex items-center gap-3">
        <span className={`h-8 w-8 rounded-full text-xs font-bold flex items-center justify-center ${avatarOf(r.address)}`}>{initials(r.name || r.address)}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-white truncate flex items-center gap-1.5">
            <span className="truncate">{r.address}</span>
            {r.is_admin && <Tag tone="amber">admin</Tag>}
            {r.hidden && <Tag tone="indigo">hidden</Tag>}
            {!r.active && <Tag tone="slate">suspended</Tag>}
            {!r.accepts_inbound && <Tag tone="rose">not receiving</Tag>}
            {!r.can_send && <Tag tone="slate">receive only</Tag>}
            {full && <Tag tone="rose">full</Tag>}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
            {r.name}{r.app ? ` · ${r.app}` : ""} · {r.total} messages · {fmtBytes(used)}
            {quotaBytes > 0 ? ` of ${r.quota_mb} MB` : " used"}
            {r.trash ? ` · ${r.trash} in bin` : ""}
          </p>
          {quotaBytes > 0 && (
            <div className="mt-1 h-1 w-full max-w-[240px] rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div style={{ width: `${pct}%` }} data-testid={`box-quota-${r.id}`}
                className={`h-full ${full ? "bg-rose-500" : near ? "bg-amber-500" : "bg-emerald-500"}`} />
            </div>
          )}
        </div>
        <button onClick={() => setTab(tab === "who" ? null : "who")} data-testid={`box-assign-${r.id}`}
          className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
          {assigned.length ? `${assigned.length} reader${assigned.length === 1 ? "" : "s"}` : "Assign"}
        </button>
        <button onClick={() => setTab(tab === "settings" ? null : "settings")} data-testid={`box-settings-${r.id}`}
          aria-label="Settings" title="Settings"
          className="text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400"><Settings size={15} /></button>
        {canCreate && <button onClick={onRemove} aria-label="Delete" title="Delete mailbox" className="text-slate-400 hover:text-rose-500"><Trash2 size={15} /></button>}
      </div>

      {tab === "who" && (
        <div className="mt-2 ml-11 rounded-lg border border-slate-200 dark:border-slate-700 p-2 max-h-44 overflow-y-auto">
          <p className="text-[11px] text-slate-400 px-2 pb-1">Everyone ticked here opens this mailbox from their own CRM sign-in.</p>
          {people.map((p) => (
            <label key={p.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
              <input type="checkbox" checked={ids.includes(p.id)} onChange={() => toggleUser(p.id)}
                data-testid={`box-assign-${r.id}-${p.id}`} />
              <span className="text-xs text-slate-700 dark:text-slate-200">{p.name}</span>
              <span className="text-[10px] text-slate-400">{p.username}</span>
            </label>
          ))}
          {!people.length && <p className="text-xs text-slate-400 p-2">No accounts to assign.</p>}
        </div>
      )}

      {tab === "settings" && (
        <div className="mt-2 ml-11 rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-[11px] text-slate-500 dark:text-slate-400 mb-1">Display name</span>
              <input value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                onBlur={() => draft.name !== r.name && save({ name: draft.name })}
                data-testid={`box-name-${r.id}`}
                className="w-full px-2 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-xs text-slate-900 dark:text-slate-100 outline-none" />
            </label>
            <label className="block">
              <span className="block text-[11px] text-slate-500 dark:text-slate-400 mb-1">App / label</span>
              <input value={draft.app || ""} onChange={(e) => setDraft({ ...draft, app: e.target.value })}
                onBlur={() => draft.app !== r.app && save({ app: draft.app })}
                data-testid={`box-app-${r.id}`}
                className="w-full px-2 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-xs text-slate-900 dark:text-slate-100 outline-none" />
            </label>
          </div>

          <label className="block">
            <span className="block text-[11px] text-slate-500 dark:text-slate-400 mb-1">
              {canCreate ? "Storage limit in MB — 0 means no limit." : "Storage limit"}
              {" "}Currently holding {fmtBytes(used)}.
            </span>
            <div className="flex items-center gap-2">
              {canCreate ? (
                <>
                  <input type="number" min="0" max="102400" value={draft.quota_mb ?? 0}
                    onChange={(e) => setDraft({ ...draft, quota_mb: e.target.value })}
                    data-testid={`box-quota-input-${r.id}`}
                    className="w-28 px-2 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-xs text-slate-900 dark:text-slate-100 outline-none" />
                  <button onClick={() => save({ quota_mb: parseInt(draft.quota_mb, 10) || 0 })} disabled={saving}
                    data-testid={`box-quota-save-${r.id}`}
                    className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700">Set</button>
                </>
              ) : (
                <span className="text-xs text-slate-600 dark:text-slate-300" data-testid={`box-quota-ro-${r.id}`}>
                  {r.quota_mb > 0 ? `${r.quota_mb} MB` : "No limit"}
                </span>
              )}
              {!!r.trash && (
                <button onClick={onEmptyBin} data-testid={`box-empty-${r.id}`}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10">
                  Empty bin ({r.trash})
                </button>
              )}
            </div>
          </label>

          <Switch label="Accepts incoming mail" testid={`box-in-${r.id}`} disabled={!canCreate}
            hint={canCreate
              ? "Off turns deliveries away and keeps nothing. Mail already here is untouched."
              : "Set by whoever manages the mailboxes themselves."}
            checked={r.accepts_inbound !== false}
            onChange={(v) => save({ accepts_inbound: v })} />
          <Switch label="Can send mail" testid={`box-send-${r.id}`} disabled={!canCreate}
            hint={canCreate
              ? "Off makes this a read-only mailbox, for an address that only ever receives."
              : "Set by whoever manages the mailboxes themselves."}
            checked={r.can_send !== false}
            onChange={(v) => save({ can_send: v })} />
          {canCreate && (
            <Switch label="Hidden from other administrators" testid={`box-hidden-${r.id}`}
              hint="Only you can see this mailbox. It keeps receiving and sending exactly as before."
              checked={!!r.hidden}
              onChange={(v) => save({ hidden: v })} />
          )}
          <Switch label="Active" testid={`box-active-${r.id}`}
            hint="Suspending hides it from everyone assigned to it and stops delivery, without deleting a thing."
            checked={r.active !== false}
            onChange={(v) => save({ active: v })} />

          <label className="block">
            <span className="block text-[11px] text-slate-500 dark:text-slate-400 mb-1">
              Signature — added to the bottom of everything this mailbox sends. Basic HTML is allowed.
            </span>
            <textarea rows={3} value={draft.signature_html || ""}
              onChange={(e) => setDraft({ ...draft, signature_html: e.target.value })}
              onBlur={() => draft.signature_html !== r.signature_html && save({ signature_html: draft.signature_html })}
              data-testid={`box-sig-${r.id}`}
              placeholder="Kind regards,<br>The Support Team"
              className="w-full px-2 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-xs font-mono text-slate-900 dark:text-slate-100 outline-none" />
          </label>

          {canCreate && (
            <button onClick={onResetPw} data-testid={`box-pw-${r.id}`}
              className="text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-600">Set a mailbox password…</button>
          )}
        </div>
      )}
    </div>
  );
}

function Tag({ tone, children }) {
  const tones = {
    amber: "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300",
    rose: "bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300",
    slate: "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300",
    indigo: "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300",
  };
  return <span className={`shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${tones[tone] || tones.slate}`}>{children}</span>;
}

function Switch({ label, hint, checked, onChange, testid, disabled }) {
  return (
    <label className={`flex items-start gap-2 ${disabled ? "cursor-default" : "cursor-pointer"}`}>
      <input type="checkbox" checked={!!checked} disabled={!!disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid} className="mt-0.5" />
      <span className="min-w-0">
        <span className={`block text-xs font-medium ${disabled
          ? "text-slate-500 dark:text-slate-400" : "text-slate-700 dark:text-slate-200"}`}>{label}</span>
        {hint && <span className="block text-[11px] text-slate-400">{hint}</span>}
      </span>
    </label>
  );
}

/* =========================== one rendered message =========================== */
function Body({ msg, dark }) {
  const [showImages, setShowImages] = useState(false);
  const frame = useRef(null);
  const [h, setH] = useState(200);
  const src = useMemo(() => {
    const raw = msg.html && msg.html.trim()
      ? msg.html
      : `<pre>${(msg.text || "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]))}</pre>`;
    return sanitizeEmailHtml(raw, showImages, msg.attachments);
  }, [msg, showImages]);
  const resize = () => {
    try {
      const d = frame.current?.contentDocument;
      if (d?.body) setH(Math.min(Math.max(d.body.scrollHeight + 24, 120), 20000));
    } catch { /* keep the default */ }
  };
  useEffect(() => { setH(200); }, [msg.id, dark]);
  // Files displayed in the body are not listed again underneath.
  const shown = (msg.attachments || []).filter((a) => !src.inlined?.has(a.id));
  return (
    <>
      {src.blocked > 0 && !showImages && (
        <div className="mx-4 mt-3 flex items-center gap-2 text-xs bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-300 rounded-lg px-3 py-2">
          <ShieldAlert size={14} />
          <span className="flex-1">{src.blocked} remote image{src.blocked > 1 ? "s" : ""} blocked to protect your privacy.</span>
          <button onClick={() => setShowImages(true)} className="font-semibold underline">Show images</button>
        </div>
      )}
      <iframe ref={frame} title={`message-${msg.id}`} onLoad={resize}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        srcDoc={frameDoc(src.html, dark)} style={{ height: h }}
        className="w-full border-0 bg-white dark:bg-slate-900" />
      {shown.length > 0 && (
        <div className="px-4 pb-4">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">{shown.length} attachment{shown.length > 1 ? "s" : ""}</p>
          <div className="flex flex-wrap gap-2">
            {shown.map((a) => (
              <a key={a.id} href={`${API}/attachments/${a.id}?download=1&t=${encodeURIComponent(tok.get() || "")}`}
                className="inline-flex items-center gap-2 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 hover:border-indigo-400">
                <Paperclip size={13} />
                <span className="max-w-[180px] truncate text-slate-700 dark:text-slate-200">{a.name}</span>
                <span className="text-slate-400">{fmtBytes(a.size)}</span>
                <Download size={13} className="text-slate-400" />
              </a>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/* =========================== reading pane =========================== */
function MessageView({ msg, thread, dark, onBack, onReply, onReplyAll, onForward, onDelete, onStar, onUnread, onArchive }) {
  const items = thread && thread.length ? thread : [msg];
  const [openIds, setOpenIds] = useState(() => new Set([msg.id]));
  useEffect(() => { setOpenIds(new Set([msg.id])); }, [msg.id]);
  const toggle = (id) => setOpenIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="flex-1 min-w-0 flex flex-col bg-white dark:bg-slate-900" data-testid="mail-view">
      <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-200 dark:border-slate-800">
        <button onClick={onBack} aria-label="Back" className="md:hidden text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mr-1"><ChevronLeft size={20} /></button>
        <h2 className="flex-1 min-w-0 truncate font-semibold text-slate-900 dark:text-white">
          {msg.subject || "(no subject)"}
          {items.length > 1 && <span className="ml-2 text-xs font-normal text-slate-400">{items.length} messages</span>}
        </h2>
        <button onClick={() => onArchive(msg)} title="Archive (e)" aria-label="Archive" className="p-1.5 rounded text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"><Archive size={17} /></button>
        <button onClick={() => onUnread(msg)} title="Mark unread (u)" aria-label="Mark unread" className="p-1.5 rounded text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"><MailOpen size={17} /></button>
        <button onClick={() => onStar(msg)} title="Star (s)" aria-label="Star" className={`p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 ${msg.starred ? "text-amber-500" : "text-slate-400 hover:text-amber-500"}`}>
          <Star size={17} fill={msg.starred ? "currentColor" : "none"} />
        </button>
        <button onClick={() => onDelete(msg)} title="Delete (#)" aria-label="Delete" className="p-1.5 rounded text-slate-400 hover:text-rose-500 hover:bg-slate-100 dark:hover:bg-slate-800"><Trash2 size={17} /></button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {items.map((m, i) => {
          const open = openIds.has(m.id) || items.length === 1;
          return (
            <div key={m.id} className={i > 0 ? "border-t border-slate-200 dark:border-slate-800" : ""}>
              <button onClick={() => items.length > 1 && toggle(m.id)}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 ${items.length > 1 ? "hover:bg-slate-50 dark:hover:bg-slate-800/60" : "cursor-default"}`}>
                <span className={`h-9 w-9 rounded-full text-sm font-bold flex items-center justify-center shrink-0 ${avatarOf(m.from_addr)}`}>
                  {initials(displayFrom(m))}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-slate-900 dark:text-white">
                    <span className="font-semibold">{displayFrom(m)}</span>{" "}
                    <span className="text-slate-500 dark:text-slate-400">&lt;{m.from_addr}&gt;</span>
                  </span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">to {m.to_str || (m.to || []).join(", ")}</span>
                  {!open && <span className="block text-xs text-slate-400 truncate mt-0.5">{m.snippet}</span>}
                </span>
                <span className="text-xs text-slate-400 whitespace-nowrap">{fmtFull(m.date)}</span>
              </button>
              {open && <Body msg={m} dark={dark} />}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-200 dark:border-slate-800">
        <button onClick={() => onReply(msg)} data-testid="mail-reply"
          className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2">
          <Reply size={15} /> Reply
        </button>
        <button onClick={() => onReplyAll(msg)} data-testid="mail-replyall"
          className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2">
          <ReplyAll size={15} /> Reply all
        </button>
        <button onClick={() => onForward(msg)}
          className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2">
          <Forward size={15} /> Forward
        </button>
      </div>
    </div>
  );
}

/* =========================== main =========================== */
export default function EmailClient() {
  const saved = prefs.get();
  const [me, setMe] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [scope, setScope] = useState("");
  const [folder, setFolder] = useState("inbox");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(saved.sort || "date_desc");
  const [group, setGroup] = useState(saved.group !== false);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [onlyAttach, setOnlyAttach] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [sel, setSel] = useState(null);
  const [thread, setThread] = useState([]);
  const [picked, setPicked] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [compose, setCompose] = useState(null);
  const [manage, setManage] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [help, setHelp] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const searchRef = useRef(null);

  useEffect(() => { prefs.set({ sort, group }); }, [sort, group]);
  useEffect(() => {
    const o = new MutationObserver(() => setDark(document.documentElement.classList.contains("dark")));
    o.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => o.disconnect();
  }, []);
  useEffect(() => {
    const onOut = () => { setMe(false); setSel(null); };
    window.addEventListener("mail-signed-out", onOut);
    return () => window.removeEventListener("mail-signed-out", onOut);
  }, []);
  useEffect(() => {
    let live = true;
    (async () => {
      // The CRM session wins whenever there is one. A mailbox session left in
      // this browser by an earlier password sign-in must not quietly stand in
      // for the person actually signed in to the CRM: that is how a
      // super-administrator arrives here as postmaster@ and finds it cannot
      // create a mailbox, holding rights the session it is using does not have.
      // tryCrmSession returns null when there is no CRM token, or when the
      // mail Sign out button was used, so a password-only session is untouched.
      const viaCrm = await tryCrmSession();
      if (viaCrm) { if (live) setMe(viaCrm); return; }
      if (tok.get()) {
        try {
          const d = await call("/me");
          if (live) setMe(d.mailbox);
          return;
        } catch { tok.clear(); }
      }
      if (live) setMe(false);
    })();
    return () => { live = false; };
  }, []);

  const loadBoxes = useCallback(async () => {
    // Everyone: an ordinary account needs the mailboxes assigned to it, which is
    // exactly what the server returns. Only an administrator gets all of them.
    if (!me) return;
    try { setBoxes((await call("/mailboxes")).mailboxes || []); } catch { /* non-fatal */ }
  }, [me]);
  useEffect(() => { loadBoxes(); }, [loadBoxes]);
  // Owning no mailbox, an all-access session would otherwise open on nothing.
  useEffect(() => {
    if (me && !me.address && !scope && boxes.length) setScope(boxes[0].id);
  }, [me, boxes, scope]);

  const loadCounts = useCallback(async () => {
    if (!me) return;
    try {
      const p = new URLSearchParams();
      if (scope) p.set("mailbox", scope);
      setCounts((await call(`/counts?${p}`)).counts || {});
    } catch { /* non-fatal */ }
  }, [me, scope]);

  const load = useCallback(async () => {
    if (!me) return;
    setLoading(true); setErr("");
    try {
      const p = new URLSearchParams({ folder, q, sort, page: String(page), page_size: "50" });
      if (scope) p.set("mailbox", scope);
      if (group) p.set("group", "1");
      if (onlyUnread) p.set("unread", "1");
      if (onlyAttach) p.set("attachments", "1");
      const d = await call(`/messages?${p}`);
      setList(d.messages || []);
      setTotal(d.total || 0);
      setPages(d.pages || 1);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, [me, folder, q, sort, page, scope, group, onlyUnread, onlyAttach]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts, list]);
  useEffect(() => { setSel(null); setPicked(new Set()); setPage(1); }, [folder, scope, q, onlyUnread, onlyAttach]);

  useEffect(() => {
    if (!me) return;
    const id = setInterval(() => { if (folder === "inbox" && !compose) { load(); loadCounts(); } }, 60000);
    return () => clearInterval(id);
  }, [me, folder, compose, load, loadCounts]);

  const open = useCallback(async (m) => {
    try {
      const full = await call(`/messages/${m.id}`);
      setSel(full);
      setList((L) => L.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
      if (m.thread_key && (m.thread_count || 0) > 1) {
        const p = new URLSearchParams();
        if (scope) p.set("mailbox", scope);
        const t = await call(`/threads/${m.thread_key}?${p}`);
        setThread(t.messages || []);
      } else setThread([]);
    } catch (e) { setErr(e.message); }
  }, [scope]);

  const star = async (m) => {
    const v = !m.starred;
    setList((L) => L.map((x) => (x.id === m.id ? { ...x, starred: v } : x)));
    setSel((s) => (s && s.id === m.id ? { ...s, starred: v } : s));
    try { await call(`/messages/${m.id}`, { method: "PATCH", json: { starred: v } }); } catch { load(); }
  };
  const markUnread = async (m) => {
    try { await call(`/messages/${m.id}`, { method: "PATCH", json: { read: false } }); setSel(null); load(); loadCounts(); } catch (e) { setErr(e.message); }
  };
  const archive = async (m) => {
    try { await call(`/messages/${m.id}`, { method: "PATCH", json: { folder: "archive" } }); setSel(null); load(); loadCounts(); } catch (e) { setErr(e.message); }
  };
  const del = async (m) => {
    try { await call(`/messages/${m.id}`, { method: "DELETE" }); setSel(null); load(); loadCounts(); } catch (e) { setErr(e.message); }
  };
  const bulk = async (action) => {
    const ids = [...picked];
    if (!ids.length) return;
    if (action === "delete" && !window.confirm(`Permanently delete ${ids.length} conversation(s)?`)) return;
    // While the list shows conversations, a ticked row means the whole thread.
    const body = { ids, action };
    if (group) {
      const keys = list.filter((m) => picked.has(m.id) && m.thread_key).map((m) => m.thread_key);
      if (keys.length) body.thread_keys = [...new Set(keys)];
    }
    try { await call("/messages/bulk", { method: "POST", json: body }); setPicked(new Set()); setSel(null); load(); loadCounts(); }
    catch (e) { setErr(e.message); }
  };
  const signOut = () => {
    // Remember the intent: without this the CRM bridge would sign an entitled
    // account straight back in, and the button would look broken.
    try { localStorage.setItem(NOAUTO_KEY, "1"); } catch { /* private mode */ }
    tok.clear(); setMe(false); setSel(null); setList([]);
  };

  const quoted = (m) =>
    `<br><br><blockquote>On ${fmtFull(m.date)}, ${displayFrom(m)} &lt;${m.from_addr}&gt; wrote:<br>${m.html || (m.text || "")}</blockquote>`;
  const replyTo = (m, all) => {
    const others = [...(m.to || []), ...(m.cc || [])].filter(
      (a) => a && a.toLowerCase() !== (me?.address || "").toLowerCase() && a.toLowerCase() !== (m.from_addr || "").toLowerCase());
    setCompose({
      to: m.from_addr,
      cc: all ? others.join(", ") : "",
      subject: /^re:/i.test(m.subject || "") ? m.subject : `Re: ${m.subject || ""}`,
      html: quoted(m), inReplyTo: m.id,
    });
  };
  const openDraft = async (m) => {
    try {
      const full = await call(`/messages/${m.id}`);
      setCompose({ draftId: full.id, to: full.to_raw || (full.to || []).join(", "),
        cc: full.cc_raw || (full.cc || []).join(", "), subject: full.subject === "(no subject)" ? "" : full.subject,
        html: full.html, attachments: full.attachments || [] });
    } catch (e) { setErr(e.message); }
  };

  /* keyboard shortcuts, the Gmail set people actually use */
  useEffect(() => {
    if (!me) return;
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (e.key === "Escape") { setSortOpen(false); setHelp(false); if (sel) setSel(null); return; }
      if (typing || compose || manage) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const idx = sel ? list.findIndex((x) => x.id === sel.id) : -1;
      if (e.key === "/") { e.preventDefault(); searchRef.current?.focus(); return; }
      if (e.key === "c") { e.preventDefault(); setCompose({}); return; }
      if (e.key === "?") { e.preventDefault(); setHelp((v) => !v); return; }
      if (e.key === "j" && list.length) { e.preventDefault(); open(list[Math.min(idx + 1, list.length - 1)] || list[0]); return; }
      if (e.key === "k" && list.length) { e.preventDefault(); open(list[Math.max(idx - 1, 0)] || list[0]); return; }
      if (!sel) return;
      if (e.key === "e") { e.preventDefault(); archive(sel); }
      else if (e.key === "#") { e.preventDefault(); del(sel); }
      else if (e.key === "s") { e.preventDefault(); star(sel); }
      else if (e.key === "u") { e.preventDefault(); markUnread(sel); }
      else if (e.key === "r") { e.preventDefault(); replyTo(sel, false); }
      else if (e.key === "a") { e.preventDefault(); replyTo(sel, true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // re-bound each render so it always sees current state

  if (me === null) return <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0B0F19]"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (me === false) return <SignIn onDone={(mb) => setMe(mb)} />;

  const folders = [
    { key: "inbox", label: "Inbox", icon: Inbox },
    { key: "starred", label: "Starred", icon: Star },
    { key: "sent", label: "Sent", icon: SendIcon },
    { key: "drafts", label: "Drafts", icon: FileText },
    { key: "archive", label: "Archive", icon: Archive },
    { key: "trash", label: "Trash", icon: Trash2 },
  ];
  const viewing = scope ? boxes.find((b) => b.id === scope) : null;
  const allPicked = list.length > 0 && list.every((m) => picked.has(m.id));
  const sortLabel = (SORTS.find((s) => s.k === sort) || SORTS[0]).l;

  const toggleAll = () => setPicked(allPicked ? new Set() : new Set(list.map((m) => m.id)));
  const togglePick = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-[#0B0F19]">
      <header className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <span className="h-8 w-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center shrink-0"><Mail size={17} /></span>
        <span className="font-semibold text-slate-900 dark:text-white hidden sm:block">Mail</span>
        <div className="flex-1 min-w-0 max-w-xl relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input ref={searchRef} defaultValue={q}
            onKeyDown={(e) => { if (e.key === "Enter") setQ(e.currentTarget.value); if (e.key === "Escape") e.currentTarget.blur(); }}
            placeholder="Search mail — try from:jane or has:attachment  ( / )" data-testid="mail-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/40" />
        </div>
        <button onClick={() => { load(); loadCounts(); }} title="Refresh" aria-label="Refresh" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white">
          <RefreshCw size={17} className={loading ? "animate-spin" : ""} />
        </button>
        <button onClick={() => setHelp(true)} title="Keyboard shortcuts (?)" aria-label="Keyboard shortcuts" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hidden sm:block"><Keyboard size={17} /></button>
        <button onClick={() => setPushOpen(true)} title="Notifications" aria-label="Notifications" data-testid="mail-pushbtn" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"><BellRing size={17} /></button>
        <span className="text-xs text-slate-500 dark:text-slate-400 hidden md:block truncate max-w-[200px]" data-testid="mail-whoami">{me.address}</span>
        <BackToCrm />
        <ThemeButton />
        {/* A CRM-backed session is not a login you can leave - it IS your CRM
            account. Offering "sign out" there would drop you at a mailbox
            password form you do not have a password for. Only a mailbox that
            signed in with one gets the button. */}
        {me.address && (
          <button onClick={signOut} title="Sign out" aria-label="Sign out" className="text-slate-500 dark:text-slate-400 hover:text-rose-500"><LogOut size={17} /></button>
        )}
      </header>

      <div className="flex-1 min-h-0 flex">
        <aside className="w-56 shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 hidden md:flex md:flex-col">
          <button onClick={() => setCompose({})} data-testid="mail-compose-btn"
            className="w-full mb-4 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold flex items-center justify-center gap-2">
            <Plus size={16} /> Compose
          </button>
          {folders.map((f) => {
            const c = counts[f.key] || {};
            const badge = f.key === "inbox" || f.key === "starred" ? c.unread : c.total;
            return (
              <button key={f.key} onClick={() => setFolder(f.key)} data-testid={`folder-${f.key}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2.5 mb-0.5 ${
                  folder === f.key ? "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 font-semibold"
                                   : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                <f.icon size={16} /> <span className="flex-1">{f.label}</span>
                {badge > 0 && <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">{badge}</span>}
              </button>
            );
          })}
          {(me.is_admin || me.can_assign || boxes.length > 1) && (
            <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-800">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
                {me.address ? "Administrator" : (me.all_access ? "Viewing mailbox" : "Your mailboxes")}
              </p>
              <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="mail-scope"
                className="w-full mb-2 px-2 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-xs text-slate-800 dark:text-slate-200 outline-none">
                {/* An all-access session has no mailbox of its own, so there is
                    no "mine" to offer - every option is somebody else's. */}
                {me.address
                  ? <option value="">My mailbox ({me.address})</option>
                  : !boxes.length && <option value="">No mailboxes assigned to you</option>}
                {boxes.filter((b) => b.id !== me.id).map((b) => (
                  <option key={b.id} value={b.id}>{b.address}{b.unread ? ` (${b.unread})` : ""}</option>
                ))}
              </select>
              {(me.can_assign || (me.is_admin && !me.all_access)) && (
              <button onClick={() => setManage(true)} data-testid="mail-manage"
                className="w-full px-3 py-2 rounded-lg text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2">
                <Users size={14} /> Manage mailboxes
              </button>
              )}
            </div>
          )}
        </aside>

        {/* list */}
        <div className={`${sel ? "hidden md:flex" : "flex"} w-full md:w-[26rem] shrink-0 flex-col border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900`}>
          <div className="md:hidden flex items-center gap-2 p-2 border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
            {folders.map((f) => (
              <button key={f.key} onClick={() => setFolder(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${folder === f.key ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>
                {f.label}{(counts[f.key]?.unread || 0) > 0 ? ` ${counts[f.key].unread}` : ""}
              </button>
            ))}
            <button onClick={() => setCompose({})} className="px-3 py-1.5 rounded-full text-xs bg-indigo-600 text-white whitespace-nowrap">Compose</button>
          </div>

          {/* toolbar: select-all, bulk actions, sort, filters */}
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-200 dark:border-slate-800 relative">
            <button onClick={toggleAll} title="Select all" aria-label="Select all" data-testid="mail-selectall"
              className="p-1.5 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
              {allPicked ? <CheckSquare size={16} /> : <Square size={16} />}
            </button>
            {picked.size > 0 ? (
              <div className="flex items-center gap-0.5" data-testid="mail-bulkbar">
                <span className="text-xs text-slate-500 dark:text-slate-400 px-1">{picked.size}</span>
                <button onClick={() => bulk("read")} title="Mark read" className="p-1.5 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><Check size={16} /></button>
                <button onClick={() => bulk("unread")} title="Mark unread" className="p-1.5 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><MailOpen size={16} /></button>
                <button onClick={() => bulk("star")} title="Star" className="p-1.5 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><Star size={16} /></button>
                <button onClick={() => bulk("archive")} title="Archive" className="p-1.5 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><Archive size={16} /></button>
                <button onClick={() => bulk(folder === "trash" ? "delete" : "trash")} title="Delete" className="p-1.5 rounded text-slate-500 dark:text-slate-400 hover:text-rose-500 hover:bg-slate-100 dark:hover:bg-slate-800"><Trash2 size={16} /></button>
              </div>
            ) : (
              <>
                <button onClick={() => setOnlyUnread((v) => !v)} data-testid="filter-unread"
                  className={`px-2 py-1 rounded text-xs ${onlyUnread ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 font-semibold" : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>Unread</button>
                <button onClick={() => setOnlyAttach((v) => !v)} data-testid="filter-attach"
                  className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${onlyAttach ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 font-semibold" : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                  <Paperclip size={11} /> Files
                </button>
                <button onClick={() => setGroup((v) => !v)} title="Group into conversations" data-testid="toggle-group"
                  className={`p-1.5 rounded ${group ? "text-indigo-600 dark:text-indigo-400" : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                  <MessagesSquare size={15} />
                </button>
              </>
            )}
            <div className="flex-1" />
            <button onClick={() => setSortOpen((v) => !v)} data-testid="mail-sort"
              className="flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
              <ArrowUpDown size={13} /> <span className="hidden sm:inline">{sortLabel}</span>
            </button>
            {sortOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setSortOpen(false)} />
                <div className="absolute right-2 top-full mt-1 z-20 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-1" data-testid="mail-sortmenu">
                  {SORTS.map((s) => (
                    <button key={s.k} onClick={() => { setSort(s.k); setSortOpen(false); setPage(1); }}
                      className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 ${sort === s.k ? "text-indigo-600 dark:text-indigo-400 font-semibold" : "text-slate-600 dark:text-slate-300"}`}>
                      {sort === s.k ? <Check size={12} /> : <span className="w-3" />} {s.l}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {viewing && (
            <div className="px-3 py-2 text-xs bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border-b border-amber-200 dark:border-amber-500/30">
              {/* An agent opening a mailbox assigned to her is not administering
                  anything - she is reading her own mail. Only say "as
                  administrator" to somebody who actually is one. */}
              Viewing <b>{viewing.address}</b>{me.all_access ? " as administrator" : ""}
            </div>
          )}
          {err && <div className="px-3 py-2 text-xs text-rose-600 dark:text-rose-400" role="alert">{err}</div>}

          <div className="flex-1 overflow-y-auto" data-testid="mail-list">
            {loading && !list.length && (
              <div className="p-3 space-y-3">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex gap-3 animate-pulse">
                    <div className="h-9 w-9 rounded-full bg-slate-200 dark:bg-slate-800" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded w-1/3" />
                      <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loading && !list.length && (
              <div className="p-10 text-center">
                <Inbox size={28} className="mx-auto text-slate-300 dark:text-slate-700 mb-2" />
                <p className="text-sm text-slate-500 dark:text-slate-400">{q || onlyUnread || onlyAttach ? "Nothing matches those filters." : `${folders.find((f) => f.key === folder)?.label} is empty.`}</p>
                {(q || onlyUnread || onlyAttach) && (
                  <button onClick={() => { setQ(""); setOnlyUnread(false); setOnlyAttach(false); if (searchRef.current) searchRef.current.value = ""; }}
                    className="mt-2 text-xs text-indigo-600 hover:underline">Clear filters</button>
                )}
              </div>
            )}
            {list.map((m) => (
              <div key={m.id}
                className={`flex items-start gap-2 pl-2 pr-3 py-2.5 border-b border-slate-100 dark:border-slate-800 cursor-pointer ${
                  sel?.id === m.id ? "bg-indigo-50 dark:bg-indigo-500/10" : picked.has(m.id) ? "bg-slate-50 dark:bg-slate-800/60" : "hover:bg-slate-50 dark:hover:bg-slate-800/60"}`}
                onClick={() => (folder === "drafts" ? openDraft(m) : open(m))}>
                <button onClick={(e) => { e.stopPropagation(); togglePick(m.id); }} aria-label="Select"
                  className="p-1 text-slate-400 hover:text-indigo-600 shrink-0" data-testid={`pick-${m.id}`}>
                  {picked.has(m.id) ? <CheckSquare size={15} /> : <Square size={15} />}
                </button>
                <button onClick={(e) => { e.stopPropagation(); star(m); }} aria-label="Star"
                  className={`p-1 shrink-0 ${m.starred ? "text-amber-500" : "text-slate-300 dark:text-slate-600 hover:text-amber-500"}`}>
                  <Star size={15} fill={m.starred ? "currentColor" : "none"} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`flex-1 min-w-0 truncate text-sm ${m.read ? "text-slate-700 dark:text-slate-300" : "font-bold text-slate-900 dark:text-white"}`}>
                      {folder === "sent" || folder === "drafts" ? `To ${m.to_str || "(no recipient)"}` : displayFrom(m)}
                      {(m.thread_count || 0) > 1 && <span className="ml-1.5 text-xs font-normal text-slate-400">({m.thread_count})</span>}
                    </span>
                    <span className="text-[11px] text-slate-400 whitespace-nowrap">{fmtDate(m.date)}</span>
                  </div>
                  <div className={`truncate text-sm ${m.read ? "text-slate-600 dark:text-slate-400" : "font-semibold text-slate-900 dark:text-slate-100"}`}>
                    {m.subject || "(no subject)"}
                  </div>
                  <div className="truncate text-xs text-slate-400">{m.snippet}</div>
                  {(m.attachments || []).length > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 mt-1"><Paperclip size={11} />{m.attachments.length}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400">
              <span data-testid="mail-range">Page {page} of {pages} · {total} total</span>
              <div className="flex gap-1">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev"
                  className="p-1 rounded disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800"><ChevronLeft size={15} /></button>
                <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="page-next"
                  className="p-1 rounded disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800"><ChevronRight size={15} /></button>
              </div>
            </div>
          )}
        </div>

        {sel ? (
          <MessageView msg={sel} thread={thread} dark={dark}
            onBack={() => setSel(null)} onStar={star} onDelete={del} onUnread={markUnread} onArchive={archive}
            onReply={(m) => replyTo(m, false)} onReplyAll={(m) => replyTo(m, true)}
            onForward={(m) => setCompose({ to: "", subject: /^fwd:/i.test(m.subject || "") ? m.subject : `Fwd: ${m.subject || ""}`, html: quoted(m) })} />
        ) : (
          <div className="flex-1 hidden md:flex flex-col items-center justify-center text-slate-400 text-sm gap-1">
            <Mail size={26} className="text-slate-300 dark:text-slate-700" />
            <span>Select a message to read</span>
            <span className="text-xs">or press <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800">c</kbd> to write one</span>
          </div>
        )}
      </div>

      {compose && (
        <Compose me={me} sendAs={scope} boxes={boxes} initial={compose}
          onClose={() => { setCompose(null); load(); loadCounts(); }}
          onSent={() => { setCompose(null); setFolder("sent"); load(); loadCounts(); }} />
      )}
      {manage && <Mailboxes me={me} onClose={() => setManage(false)} onChanged={loadBoxes} />}
      {pushOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPushOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="mail-pushpanel"
            className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 w-full max-w-md">
            <h2 className="font-semibold text-slate-900 dark:text-white mb-1">Notifications</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Get a notification on this device when new mail arrives.</p>
            <PushToggle token={tok.get()} compact />
            <button onClick={() => setPushOpen(false)} className="mt-5 w-full py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200">Close</button>
          </div>
        </div>
      )}
      {help && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setHelp(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 w-full max-w-md">
            <h2 className="font-semibold text-slate-900 dark:text-white mb-4">Keyboard shortcuts</h2>
            <div className="grid grid-cols-2 gap-y-2 text-sm text-slate-600 dark:text-slate-300">
              {[["c", "Compose"], ["/", "Search"], ["j / k", "Next / previous"], ["r", "Reply"], ["a", "Reply all"],
                ["e", "Archive"], ["s", "Star"], ["u", "Mark unread"], ["#", "Delete"], ["Esc", "Close"]].map(([k, v]) => (
                <React.Fragment key={k}>
                  <kbd className="justify-self-start px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-xs">{k}</kbd>
                  <span>{v}</span>
                </React.Fragment>
              ))}
            </div>
            <button onClick={() => setHelp(false)} className="mt-5 w-full py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
